import { useCallback, useRef, useState, type MutableRefObject } from "react";
import { normalizePttQuery } from "../../pttQueryNormalize";
import type { PttState, PttTurn } from "./types";

export type PttTurnSession = {
  pttState: PttState;
  turnHistory: PttTurn[];
  error: string | null;
  busyRef: MutableRefObject<boolean>;
  addPendingTurn: (query: string) => string;
  updateTurnQuery: (id: string, query: string) => void;
  dropTurn: (id: string) => void;
  runQueryForTurn: (turnId: string, rawQuery: string) => Promise<void>;
  handleResult: (rawQuery: string) => Promise<void>;
  runTextCommand: (text: string) => Promise<void>;
  clearHistory: () => void;
  setRecording: () => void;
  setProcessing: () => void;
  setIdle: () => void;
  setError: (message: string | null) => void;
};

type Deps = {
  runVoiceCommand: (query: string) => Promise<string>;
  playReadback: (readback: string) => Promise<void>;
};

/** Turn history + voice command brain. Transport-agnostic. */
export function usePttTurns(deps: Deps): PttTurnSession {
  const depsRef = useRef(deps);
  depsRef.current = deps;

  const [pttState, setPttState] = useState<PttState>("idle");
  const [turnHistory, setTurnHistory] = useState<PttTurn[]>([]);
  const [error, setError] = useState<string | null>(null);
  const busyRef = useRef(false);

  const addPendingTurn = useCallback((query: string): string => {
    const id = crypto.randomUUID();
    setTurnHistory((h) => [...h, { id, query, readback: null, at: Date.now() }]);
    return id;
  }, []);

  const updateTurnQuery = useCallback((id: string, query: string) => {
    setTurnHistory((h) => h.map((t) => (t.id === id ? { ...t, query } : t)));
  }, []);

  const dropTurn = useCallback((id: string) => {
    setTurnHistory((h) => h.filter((t) => t.id !== id));
  }, []);

  const runQueryForTurn = useCallback(
    async (turnId: string, rawQuery: string) => {
      const query = normalizePttQuery(rawQuery);
      if (!query) {
        dropTurn(turnId);
        setError("No speech detected");
        return;
      }
      updateTurnQuery(turnId, query);
      try {
        const readback = await depsRef.current.runVoiceCommand(query);
        setTurnHistory((h) => h.map((t) => (t.id === turnId ? { ...t, readback } : t)));
        await depsRef.current.playReadback(readback);
      } catch (err) {
        dropTurn(turnId);
        setError(err instanceof Error ? err.message : "PTT error");
      }
    },
    [dropTurn, updateTurnQuery],
  );

  const handleResult = useCallback(
    async (rawQuery: string) => {
      const turnId = addPendingTurn(rawQuery);
      await runQueryForTurn(turnId, rawQuery);
    },
    [addPendingTurn, runQueryForTurn],
  );

  const runTextCommand = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || busyRef.current) return;
      busyRef.current = true;
      setPttState("processing");
      setError(null);
      try {
        await handleResult(trimmed);
      } finally {
        busyRef.current = false;
        setPttState("idle");
      }
    },
    [handleResult],
  );

  const clearHistory = useCallback(() => {
    setTurnHistory([]);
    setError(null);
  }, []);

  return {
    pttState,
    turnHistory,
    error,
    busyRef,
    addPendingTurn,
    updateTurnQuery,
    dropTurn,
    runQueryForTurn,
    handleResult,
    runTextCommand,
    clearHistory,
    setRecording: () => setPttState("recording"),
    setProcessing: () => setPttState("processing"),
    setIdle: () => setPttState("idle"),
    setError,
  };
}
